import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, BenefitsGrid, HowItWorks, FAQSection, CTASection } from '../../components/PageTemplate';
import { 
  Bell, Clock, CheckCircle, MapPin, Calendar, 
  MessageCircle, Camera, Star 
} from 'lucide-react';

export default function ContractorsJobs() {
  const jobSteps = [
    {
      title: 'Job Alert',
      description: 'You receive a notification for a new job in your area and trade.'
    },
    {
      title: 'Review Details',
      description: 'See the issue, photos, location, budget, and any special requirements.'
    },
    {
      title: 'Accept or Decline',
      description: 'If it fits your schedule, accept. First suitable contractor wins.'
    },
    {
      title: 'Schedule Visit',
      description: 'Contact the tenant directly to arrange a convenient time.'
    },
    {
      title: 'Complete Work',
      description: 'Do the job, upload before/after photos as evidence.'
    },
    {
      title: 'Mark Complete',
      description: 'Tenant confirms the work. Job closed and payment processed.'
    }
  ];

  const features = [
    {
      icon: Bell,
      title: 'Instant Notifications',
      description: 'Get alerted the moment a relevant job is posted. Speed is key.'
    },
    {
      icon: MapPin,
      title: 'Location Filtering',
      description: 'Only receive jobs within your service area. No wasted time.'
    },
    {
      icon: Clock,
      title: 'SLA Tracking',
      description: 'See response and completion time expectations. Meet them to keep high ratings.'
    },
    {
      icon: Calendar,
      title: 'Schedule Management',
      description: 'Set your availability. Only get jobs when you\'re free to take them.'
    },
    {
      icon: MessageCircle,
      title: 'Direct Communication',
      description: 'Message tenants and landlords directly through the app.'
    },
    {
      icon: Camera,
      title: 'Photo Evidence',
      description: 'Upload before/after photos. Protects you and proves quality work.'
    },
    {
      icon: CheckCircle,
      title: 'Job Tracking',
      description: 'See all your active, pending, and completed jobs in one place.'
    },
    {
      icon: Star,
      title: 'Build Ratings',
      description: 'Great work earns great reviews. High ratings = more jobs.'
    }
  ];

  const faqs = [
    {
      question: 'How does "first-to-accept" work?',
      answer: 'When a job is posted, all suitable contractors in the area are notified. The first one to accept (who meets all requirements) gets the job. Speed matters!'
    },
    {
      question: 'What if I need to cancel a job I accepted?',
      answer: 'You can withdraw before scheduling. After scheduling, cancellations affect your rating. Repeat cancellations may result in removal from the network.'
    },
    {
      question: 'What are the SLA requirements?',
      answer: 'Emergency jobs: respond within 4 hours, complete within 24-48 hours. Standard jobs: respond within 24 hours, complete within 5 working days. Specific SLAs shown on each job.'
    },
    {
      question: 'What if the job is bigger than expected?',
      answer: 'If the scope changes or additional work is needed, submit a revised quote through the app. Work beyond the original scope needs landlord approval before proceeding.'
    },
    {
      question: 'How do I communicate with tenants?',
      answer: 'Use the in-app messaging to arrange access times, provide updates, and confirm completion. All communication is logged.'
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="JOBS & SCHEDULING"
        title="Get Jobs,"
        subtitle="Your Way"
        description="Receive instant job alerts, accept the ones that fit your schedule, communicate directly with tenants, and build your reputation."
        primaryCTA={{ href: '/contractor/register', label: 'Join Network' }}
        secondaryCTA={{ href: '/contractors', label: 'Back to Overview' }}
        gradient="from-blue-50 via-white to-slate-50"
      />

      <BenefitsGrid title="Job Management Features" benefits={features} />

      <HowItWorks title="How Jobs Work" steps={jobSteps} />

      {/* SLA Examples */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-8">
            Service Level Expectations
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="p-6 bg-red-50 rounded-xl border border-red-200">
              <h3 className="font-bold text-red-800 mb-3">Emergency Jobs</h3>
              <ul className="space-y-2 text-sm text-red-700">
                <li>• Respond within 4 hours</li>
                <li>• On-site within 24 hours</li>
                <li>• Complete within 48 hours</li>
                <li>• Examples: No heating, water leaks, security</li>
              </ul>
            </div>
            <div className="p-6 bg-green-50 rounded-xl border border-green-200">
              <h3 className="font-bold text-green-800 mb-3">Standard Jobs</h3>
              <ul className="space-y-2 text-sm text-green-700">
                <li>• Respond within 24 hours</li>
                <li>• Schedule within 3 days</li>
                <li>• Complete within 5 working days</li>
                <li>• Examples: Dripping taps, minor repairs</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <CTASection
        title="Ready to start receiving jobs?"
        description="Join our network and grow your business with steady work."
        primaryCTA={{ href: '/contractor/register', label: 'Apply Now' }}
      />
    </PublicLayout>
  );
}
